import React, { useCallback, useEffect, useState } from 'react';
import { X, ArrowRight, Pencil, Trash2, Plus, MapPin, Eye, Bell, BellOff, Phone } from 'lucide-react';
import { numberToPersianWords } from './persianMoney.jsx';
import LocationPicker from './locationPicker.jsx';
import { AreaPreview, AreasOverview } from './areaMap.jsx';
import { STEPS, getStepSummary } from './requestWizard.jsx';
import { phoneText, telHref } from './navBus.jsx';
import './consultantBoard.scss';

// کارتابلِ مشاور: محدوده‌های کاری (نقشه‌ی همیشگی بالا)، و درخواست‌های اجاره / خرید / فروش داخلِ همان محدوده‌ها

const TABS = [
  { id: 'rent', label: 'اجاره', dealType: 'rent' },
  { id: 'sale', label: 'خرید', dealType: 'sale' },
  { id: 'sell', label: 'فروش', dealType: 'sell' },
];
const PROPERTY = { apartment: 'آپارتمان', villa: 'ویلایی', land: 'زمین', office: 'اداری', shop: 'مغازه' };
const AMENITY = { elevator: 'آسانسور', parking: 'پارکینگ', storage: 'انباری', balcony: 'بالکن' };
const SEND_LABELS = {
  free: 'ارسال رایگان به مشاوران داخل اپلیکیشن',
  paid: 'ارسال پیامکی (غیررایگان) به تمام مشاوران منطقه',
};
const FIRST_TIME_TEXT =
  'محدوده‌ی کاری خودتان را روی نقشه مشخص کنید. می‌توانید چند محدوده‌ی جدا اضافه کنید. فقط درخواست‌های داخلِ این محدوده‌ها به شما نشان داده می‌شود.';

const fa = (v) => {
  const s = String(v ?? '');
  const n = Number(s.replace(/,/g, ''));
  return s.trim() !== '' && Number.isFinite(n) ? n.toLocaleString('fa-IR') : s;
};

function range(from, to, unit = '') {
  const suffix = unit ? ` ${unit}` : '';
  if (from && to) return `${fa(from)} تا ${fa(to)}${suffix}`;
  if (from) return `از ${fa(from)}${suffix}`;
  if (to) return `تا ${fa(to)}${suffix}`;
  return null;
}

function cardLines(f) {
  const lines = [];
  if (f.dealType === 'rent') {
    const dep = range(f.depositFrom, f.depositTo, 'تومان');
    const rent = range(f.rentFrom, f.rentTo, 'تومان');
    if (dep) lines.push(`ودیعه: ${dep}`);
    if (rent) lines.push(`اجاره‌ی ماهیانه: ${rent}`);
  } else {
    const price = range(f.priceFrom, f.priceTo, 'تومان');
    if (price) lines.push(`بودجه: ${price}`);
  }
  const area = range(f.areaFrom, f.areaTo, 'متر');
  if (area) lines.push(`متراژ: ${area}`);
  if (Array.isArray(f.rooms) && f.rooms.length) lines.push(`اتاق: ${f.rooms.join('، ')}`);
  const year = range(f.buildYearFrom, f.buildYearTo);
  if (year) lines.push(`سال ساخت: ${year}`);
  if (Array.isArray(f.amenities) && f.amenities.length) lines.push(`امکانات: ${f.amenities.map((a) => AMENITY[a] || a).join('، ')}`);
  return lines;
}

async function api(path, method = 'GET', body) {
  let resp;
  try {
    resp = await fetch(`/api/requests${path}`, {
      method,
      credentials: 'same-origin',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new Error('اتصال به سرور برقرار نشد؛ اینترنت را بررسی کنید.');
  }
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.message || 'خطایی پیش آمد؛ دوباره امتحان کنید.');
  return data;
}

const tabOf = (item) => (item.data?.formData?.dealType === 'rent' ? 'rent' : item.data?.formData?.dealType === 'sell' ? 'sell' : 'sale');

function RequestCard({ item, onOpen }) {
  const f = item.data?.formData || {};
  const hoods = Array.isArray(item.data?.neighborhoods) ? item.data.neighborhoods : [];
  const lines = cardLines(f);
  const date = item.createdAt ? new Date(item.createdAt).toLocaleDateString('fa-IR') : '';
  const others = item.otherViewers || 0;
  return (
    <button type="button" className="consultant-board__card" onClick={() => onOpen(item)}>
      <span className="consultant-board__card-head">
        <strong>
          {PROPERTY[f.propertyType] || 'ملک'}
          {item.isNew && <span className="consultant-board__new">جدید</span>}
        </strong>
        {date && <time>{date}</time>}
      </span>
      {hoods.length > 0 && (
        <span className="consultant-board__hoods">
          <MapPin size={13} strokeWidth={1.9} />
          <span>{hoods.join('، ')}</span>
        </span>
      )}
      {lines.map((l) => (
        <span key={l} className="consultant-board__line">
          {l}
        </span>
      ))}
      <span className="consultant-board__seen">
        <Eye size={13} strokeWidth={1.9} />
        {others > 0 ? `${fa(others)} مشاور دیگر این آگهی را دیده‌اند` : 'تاکنون مشاور دیگری این آگهی را ندیده است'}
      </span>
    </button>
  );
}

// جزئیاتِ آگهی، با چیدمانِ نزدیک به صفحه‌ی آگهی‌های دیوار: عنوان و قیمتِ بالا، جدولِ مشخصات، بخش‌های جدا با
// تیتر، نقشه‌ی محدوده، و دکمه‌ی تماسِ ثابتِ پایین
const money = (v) => (v ? fa(v) : '');
function moneyWords(from, to) {
  const a = from ? numberToPersianWords(from) : '';
  const b = to ? numberToPersianWords(to) : '';
  if (a && b) return `از ${a} تا ${b} تومان`;
  if (a) return `از ${a} تومان`;
  if (b) return `تا ${b} تومان`;
  return '';
}

function RequestDetail({ item }) {
  const f = item.data?.formData || {};
  const notes = item.data?.notes || {};
  const hoods = Array.isArray(item.data?.neighborhoods) ? item.data.neighborhoods : [];
  const others = item.otherViewers || 0;
  const isRent = f.dealType === 'rent';
  const isSell = f.dealType === 'sell';
  const dealLabel = isRent ? 'اجاره' : isSell ? 'فروش' : 'خرید';
  const prices = isRent
    ? [
        { label: 'ودیعه', from: f.depositFrom, to: f.depositTo },
        { label: 'اجاره‌ی ماهیانه', from: f.rentFrom, to: f.rentTo },
      ]
    : [{ label: 'بودجه', from: f.priceFrom, to: f.priceTo }];
  const facts = [
    range(f.areaFrom, f.areaTo, 'متر') && { label: 'متراژ', value: range(f.areaFrom, f.areaTo, 'متر') },
    Array.isArray(f.rooms) && f.rooms.length && { label: 'تعداد اتاق', value: f.rooms.join('، ') },
    range(f.buildYearFrom, f.buildYearTo) && { label: 'سال ساخت', value: range(f.buildYearFrom, f.buildYearTo) },
    { label: 'نوع ملک', value: PROPERTY[f.propertyType] || 'ملک' },
  ].filter(Boolean);
  const amenities = Array.isArray(f.amenities) ? f.amenities.map((a) => AMENITY[a] || a) : [];
  const noteItems = STEPS.filter((s) => s.id !== 'location' && notes[s.id] && String(notes[s.id]).trim());
  const phone = item.requesterPhone;

  return (
    <div className="cd">
      <section className="cd-hero">
        <div className="cd-hero__tags">
          <span className="cd-tag is-deal">{dealLabel}</span>
          {item.isNew && <span className="cd-tag is-new">جدید</span>}
        </div>
        <h2 className="cd-hero__title">{PROPERTY[f.propertyType] || 'ملک'}{hoods.length ? ` در ${hoods[0]}` : ''}</h2>
        {item.createdAt && <p className="cd-hero__date">ثبت‌شده در {new Date(item.createdAt).toLocaleString('fa-IR')}</p>}
      </section>

      <section className="cd-prices">
        {prices.map((p) =>
          p.from || p.to ? (
            <div key={p.label} className="cd-price">
              <span className="cd-price__label">{p.label}</span>
              <strong className="cd-price__value">
                {p.from && p.to ? `${money(p.from)} تا ${money(p.to)}` : p.from ? `از ${money(p.from)}` : `تا ${money(p.to)}`}
                <small> تومان</small>
              </strong>
              <span className="cd-price__words">{moneyWords(p.from, p.to)}</span>
            </div>
          ) : null
        )}
      </section>

      {facts.length > 0 && (
        <section className="cd-facts">
          {facts.map((x) => (
            <div key={x.label} className="cd-fact">
              <span>{x.label}</span>
              <strong>{x.value}</strong>
            </div>
          ))}
        </section>
      )}

      {amenities.length > 0 && (
        <section className="cd-section">
          <h3>امکانات</h3>
          <div className="cd-chips">
            {amenities.map((a) => (
              <span key={a} className="cd-chip">{a}</span>
            ))}
          </div>
        </section>
      )}

      {noteItems.length > 0 && (
        <section className="cd-section">
          <h3>توضیحات درخواست‌کننده</h3>
          {noteItems.map((s) => (
            <div key={s.id} className="cd-note">
              <span>{s.title}</span>
              <p>{notes[s.id]}</p>
            </div>
          ))}
        </section>
      )}

      {f.location && (
        <section className="cd-section">
          <h3>محدوده‌ی جست‌وجو</h3>
          <AreaPreview geojson={f.location} />
          {hoods.length > 0 && <p className="cd-hoods">{hoods.join('، ')}</p>}
        </section>
      )}

      <section className="cd-section cd-meta">
        <div>
          <span>نحوه‌ی ارسال</span>
          <p>{SEND_LABELS[item.data?.sendMethod] || SEND_LABELS.free}</p>
        </div>
        <div>
          <span>مشاورهای دیگری که این آگهی را دیده‌اند</span>
          <p>{others > 0 ? `${fa(others)} مشاور` : 'هنوز مشاور دیگری این آگهی را ندیده است'}</p>
        </div>
      </section>

      {phone && (
        <div className="cd-contact">
          <a className="cd-contact__btn" href={telHref(phone)}>
            <Phone size={18} strokeWidth={2} />
            <span>تماس با درخواست‌کننده</span>
          </a>
          <span className="cd-contact__num" dir="ltr">{phoneText(phone)}</span>
        </div>
      )}
    </div>
  );
}

function ConsultantBoard({ onClose, onChange }) {
  const [areas, setAreas] = useState(null); // null = در حال بارگذاری
  const [inbox, setInbox] = useState({ items: [], loading: true, error: '' });
  const [muted, setMuted] = useState([]); // بخش‌های بی‌صدا (اعلانِ تازه‌ها برایشان نشان داده نمی‌شود)
  // وقتی کارتابل باز است، صفحه‌ی پشتِ آن (پروفایل) اسکرول نشود
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  const [tab, setTab] = useState('rent');
  const [openItem, setOpenItem] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [picker, setPicker] = useState(null); // { index: -1 برای محدوده‌ی جدید | شماره‌ی محدوده, shape, intro }
  const [error, setError] = useState('');

  const loadInbox = useCallback(async () => {
    setInbox((s) => ({ ...s, loading: true, error: '' }));
    try {
      const r = await api('/inbox');
      setInbox({ items: r.items || [], loading: false, error: '' });
      setMuted(r.muted || []);
      onChange?.();
    } catch (e) {
      setInbox({ items: [], loading: false, error: e.message });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api('/areas')
      .then((r) => {
        if (cancelled) return;
        const list = r.items || [];
        setAreas(list);
        // بار اولی که مشاور وارد می‌شود: اگر محدوده‌ای ندارد، نقشه باز می‌شود تا محدوده‌اش را مشخص کند
        if (!list.length) setPicker({ index: -1, shape: null, intro: FIRST_TIME_TEXT });
        loadInbox();
      })
      .catch((e) => {
        if (!cancelled) {
          setAreas([]);
          setError(e.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadInbox]);

  // بی‌صدا کردنِ یک بخش (اجاره / خرید / فروش) — فقط اعلانِ همان بخش خاموش می‌شود، خودِ درخواست‌ها دیده می‌شوند
  const toggleMute = async (key) => {
    const prev = muted;
    const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
    setMuted(next);
    try {
      await api('/prefs', 'PUT', { muted: next });
      onChange?.();
    } catch (e) {
      setMuted(prev);
      setError(e.message);
    }
  };

  // آگهی وقتی واقعاً باز می‌شود «دیده‌شده» ثبت می‌شود؛ صرفِ نمایش در فهرست کافی نیست
  const openDetail = (item) => {
    setOpenItem(item);
    if (!item.isNew) return;
    api(`/${encodeURIComponent(item.id)}/view`, 'POST')
      .then(() => {
        setInbox((s) => ({ ...s, items: s.items.map((i) => (i.id === item.id ? { ...i, isNew: false } : i)) }));
        onChange?.();
      })
      .catch(() => {});
  };

  const saveAreas = async (next) => {
    setError('');
    try {
      await api('/areas', 'PUT', { areas: next.map((a) => a.geometry) });
      setAreas(next);
      loadInbox();
    } catch (e) {
      setError(e.message);
    }
  };

  const handlePickerConfirm = (feature) => {
    const target = picker;
    setPicker(null);
    if (!feature || !target) return; // بدونِ شکل: تغییری نیست
    const next = [...(areas || [])];
    if (target.index < 0) next.push({ geometry: feature.geometry });
    else next[target.index] = { geometry: feature.geometry };
    saveAreas(next);
  };

  if (areas === null) {
    return (
      <div className="consultant-board">
        <div className="consultant-board__box">
          <p className="consultant-board__empty">در حال بارگذاری…</p>
        </div>
      </div>
    );
  }

  const counts = TABS.reduce((acc, t) => ({ ...acc, [t.id]: inbox.items.filter((i) => tabOf(i) === t.id).length }), {});
  const list = inbox.items.filter((i) => tabOf(i) === tab);
  const tabLabel = TABS.find((t) => t.id === tab)?.label || '';

  return (
    <div className="consultant-board">
      <div className="consultant-board__box">
        <header className="consultant-board__header">
          {openItem ? (
            <button type="button" className="consultant-board__icon" onClick={() => setOpenItem(null)} aria-label="بازگشت">
              <ArrowRight size={20} strokeWidth={1.8} />
            </button>
          ) : (
            <span className="consultant-board__icon consultant-board__icon--ghost" />
          )}
          <strong>{openItem ? 'جزئیات درخواست' : 'کارتابل'}</strong>
          <button type="button" className="consultant-board__icon" onClick={onClose} aria-label="بستن کارتابل">
            <X size={20} strokeWidth={1.8} />
          </button>
        </header>

        <div className="consultant-board__body">
          {error && <p className="consultant-board__error">{error}</p>}

          {openItem ? (
            <RequestDetail item={openItem} />
          ) : (
            <>
              <section className="consultant-board__areas">
                <AreasOverview areas={areas} height={130} onClick={() => setManageOpen(true)} />
                <div className="consultant-board__areas-row">
                  <span>محدوده‌های کاری: {fa(areas.length)}</span>
                  <button type="button" onClick={() => setManageOpen(true)}>
                    <Pencil size={13} strokeWidth={1.9} />
                    <span>ویرایش محدوده‌ها</span>
                  </button>
                </div>
              </section>

              <nav className="consultant-board__tabs">
                {TABS.map((t) => {
                  const isMuted = muted.includes(t.id);
                  const fresh = inbox.items.filter((i) => tabOf(i) === t.id && i.isNew).length;
                  return (
                    <div key={t.id} className={`consultant-board__tab ${tab === t.id ? 'is-active' : ''}`}>
                      <button type="button" className="consultant-board__tab-main" onClick={() => setTab(t.id)}>
                        {t.label}
                        <span>{fa(counts[t.id] || 0)}</span>
                        {fresh > 0 && !isMuted && <em>{fa(fresh)} جدید</em>}
                      </button>
                      <button
                        type="button"
                        className={`consultant-board__bell ${isMuted ? 'is-muted' : ''}`}
                        onClick={() => toggleMute(t.id)}
                        aria-label={isMuted ? `روشن کردنِ اعلانِ ${t.label}` : `بی‌صدا کردنِ اعلانِ ${t.label}`}
                        title={isMuted ? 'اعلان خاموش است' : 'اعلان روشن است'}
                      >
                        {isMuted ? <BellOff size={14} strokeWidth={1.9} /> : <Bell size={14} strokeWidth={1.9} />}
                      </button>
                    </div>
                  );
                })}
              </nav>

              {inbox.loading ? (
                <p className="consultant-board__empty">در حال بارگذاری…</p>
              ) : inbox.error ? (
                <p className="consultant-board__empty">{inbox.error}</p>
              ) : areas.length === 0 ? (
                <p className="consultant-board__empty">ابتدا محدوده‌ی کاری خود را مشخص کنید.</p>
              ) : list.length === 0 ? (
                <p className="consultant-board__empty">
                  {tab === 'sell' ? 'درخواست فروشی هنوز ثبت نشده است.' : `در این بخش (${tabLabel}) درخواستی در محدوده‌های شما نیست.`}
                </p>
              ) : (
                list.map((item) => <RequestCard key={item.id} item={item} onOpen={openDetail} />)
              )}
            </>
          )}
        </div>
      </div>

      {manageOpen && (
        <div className="consultant-board__manage">
          <header className="consultant-board__header">
            <span className="consultant-board__icon consultant-board__icon--ghost" />
            <strong>محدوده‌های کاری</strong>
            <button type="button" className="consultant-board__icon" onClick={() => setManageOpen(false)} aria-label="بستن">
              <X size={20} strokeWidth={1.8} />
            </button>
          </header>
          <div className="consultant-board__body">
            {areas.map((a, i) => (
              <div key={i} className="consultant-board__area-item">
                <span>محدوده‌ی {fa(i + 1)}</span>
                <button
                  type="button"
                  onClick={() => setPicker({ index: i, shape: { type: 'Feature', properties: {}, geometry: a.geometry }, intro: null })}
                  aria-label="ویرایش"
                >
                  <Pencil size={15} strokeWidth={1.9} />
                </button>
                <button type="button" className="is-danger" onClick={() => saveAreas(areas.filter((_, j) => j !== i))} aria-label="حذف">
                  <Trash2 size={15} strokeWidth={1.9} />
                </button>
              </div>
            ))}
            <button type="button" className="consultant-board__add" onClick={() => setPicker({ index: -1, shape: null, intro: null })}>
              <Plus size={16} strokeWidth={2} />
              <span>افزودن محدوده‌ی جدید</span>
            </button>
          </div>
        </div>
      )}

      {picker && (
        <LocationPicker
          intro={picker.intro}
          initialShape={picker.shape}
          confirmLabel={picker.index < 0 ? 'افزودن این محدوده' : 'ذخیره‌ی محدوده'}
          onClose={() => setPicker(null)}
          onConfirm={handlePickerConfirm}
        />
      )}
    </div>
  );
}

export default ConsultantBoard;
